import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * ECLIPSE — Cinema Aperture / Photographer's Eye
 *
 * Mood: A photographer's camera. Black + bone + amber gold. 8-blade
 * iris aperture, exposure stops, focus brackets, photo-developing
 * reveals. Every section feels like a different exposure.
 *
 *  ACT I   HERO — 8-blade aperture OPENS on load revealing title.
 *          Camera UI elements (f-stop, ISO, focus brackets) frame it.
 *          Marquee EXIF data top/bot.
 *  ACT II  PINNED APERTURE TRANSITION — aperture CLOSES (8 blades
 *          rotate inward), then OPENS revealing a NEW scene. Three
 *          rounds back-to-back like camera shutters.
 *  ACT III FILM DEVELOPING REVEAL — photo emerges from black (invert
 *          → desaturate → full color via filter chain) on scroll.
 *          Multiple photos develop in stagger sequence.
 *  ACT IV  CONTACT SHEET — grid of 6 photos with frame numbers,
 *          each tilts on hover, scroll-reveals one by one with rotateX.
 *  ACT V   EXPOSURE DATE — date displayed as camera settings
 *          (1/250s · f/2.8 · ISO 100 style). Numbers count up.
 *  ACT VI  RSVP card with shutter-button styling, magnetic tilt.
 */
export function EclipseLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const tiltRef = useRef<HTMLDivElement>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthNum = d ? (d.getMonth() + 1).toString().padStart(2, "0") : "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const year = d?.getFullYear() ?? 2026;
  const time24 = d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "--:--";

  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 10}deg) rotateX(${-dy * 10}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: Hero aperture OPEN on load ──────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      // Aperture blades start closed (rotated to cover center)
      gsap.set(".ecl-blade", { rotate: 35, transformOrigin: "100% 100%" });
      tl.to(".ecl-blade", { rotate: -90, stagger: 0.03, duration: 2.4, ease: "expo.inOut" }, 0);

      // UI elements fade in
      tl.from(".ecl-ui-tl", { x: -20, y: -20, opacity: 0, duration: 1, ease: "power3.out" }, 0.5);
      tl.from(".ecl-ui-tr", { x: 20, y: -20, opacity: 0, duration: 1 }, 0.6);
      tl.from(".ecl-ui-bl", { x: -20, y: 20, opacity: 0, duration: 1 }, 0.7);
      tl.from(".ecl-ui-br", { x: 20, y: 20, opacity: 0, duration: 1 }, 0.8);

      tl.from(".ecl-focus-brackets > *", { scale: 0, opacity: 0, stagger: 0.1, duration: 0.5, ease: "back.out(1.8)" }, 1.0);

      // Title — letter drop with focus blur
      const titleSplit = new SplitText(".ecl-hero-title", { type: "chars" });
      gsap.set(titleSplit.chars, { filter: "blur(20px)" });
      tl.from(titleSplit.chars, { y: 30, opacity: 0, stagger: 0.04, duration: 1, ease: "power3.out" }, 1.2);
      tl.to(titleSplit.chars, { filter: "blur(0)", stagger: 0.04, duration: 0.8, ease: "power2.out" }, 1.2);

      tl.from(".ecl-hero-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 1, ease: "power3.out" }, 1.6);

      // Marquees slide in
      tl.from(".ecl-marquee-t", { x: "100%", duration: 1.6 }, 0.4);
      tl.from(".ecl-marquee-b", { x: "-100%", duration: 1.6 }, 0.5);

      // ── ACT II: PINNED APERTURE TRANSITION ────────────────────────
      const apTransition = document.querySelector<HTMLElement>(".ecl-ap-section");
      if (apTransition) {
        const apTl = gsap.timeline({
          scrollTrigger: {
            trigger: apTransition,
            start: "top top",
            end: "+=400%",
            pin: true,
            scrub: 1,
          },
        });

        // Three rounds of aperture close-open with different content behind
        // Round 1: closes, content #1 shows
        apTl.to(".ecl-ap-blade", { rotate: 35, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 0);
        apTl.to(".ecl-scene-0", { opacity: 0, duration: 0.3 }, 0.5);
        apTl.to(".ecl-scene-1", { opacity: 1, duration: 0.3 }, 0.7);
        apTl.to(".ecl-ap-blade", { rotate: -90, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 1);

        // Round 2
        apTl.to(".ecl-ap-blade", { rotate: 35, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 2.5);
        apTl.to(".ecl-scene-1", { opacity: 0, duration: 0.3 }, 3);
        apTl.to(".ecl-scene-2", { opacity: 1, duration: 0.3 }, 3.2);
        apTl.to(".ecl-ap-blade", { rotate: -90, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 3.5);

        // Round 3
        apTl.to(".ecl-ap-blade", { rotate: 35, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 5);
        apTl.to(".ecl-scene-2", { opacity: 0, duration: 0.3 }, 5.5);
        apTl.to(".ecl-scene-3", { opacity: 1, duration: 0.3 }, 5.7);
        apTl.to(".ecl-ap-blade", { rotate: -90, stagger: 0.02, ease: "power2.inOut", duration: 1 }, 6);

        // Scene caption updates
        const captions = [
          { time: "1/4000", label: "Sözleştik" },
          { time: "1/1000", label: "Yolculuk" },
          { time: "1/250", label: "Diz çöktü" },
          { time: "1/60", label: "Bugün" },
        ];
        captions.forEach((cap, i) => {
          ScrollTrigger.create({
            trigger: apTransition,
            start: `top top-=${i * 100}%`,
            end: `top top-=${(i + 1) * 100}%`,
            onEnter() {
              const timeEl = document.querySelector<HTMLElement>(".ecl-stage-time");
              const labelEl = document.querySelector<HTMLElement>(".ecl-stage-label");
              if (timeEl) timeEl.textContent = cap.time;
              if (labelEl) labelEl.textContent = cap.label;
            },
            onEnterBack() {
              const timeEl = document.querySelector<HTMLElement>(".ecl-stage-time");
              const labelEl = document.querySelector<HTMLElement>(".ecl-stage-label");
              if (timeEl) timeEl.textContent = cap.time;
              if (labelEl) labelEl.textContent = cap.label;
            },
          });
        });
      }

      // ── ACT III: FILM DEVELOPING ──────────────────────────────────
      document.querySelectorAll(".ecl-develop").forEach((img) => {
        gsap.fromTo(img,
          { filter: "invert(1) saturate(0) brightness(1.4) contrast(2)", opacity: 0.2 },
          {
            filter: "invert(0) saturate(1) brightness(1) contrast(1)",
            opacity: 1,
            duration: 1.6,
            ease: "power2.inOut",
            scrollTrigger: { trigger: img, start: "top 80%", toggleActions: "play reverse play reverse" },
          }
        );
      });

      gsap.from(".ecl-develop-label", { y: 20, opacity: 0, stagger: 0.15, duration: 0.9, ease: "power3.out",
        scrollTrigger: { trigger: ".ecl-develop-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT IV: CONTACT SHEET — each frame reveals with rotateX ─────
      document.querySelectorAll(".ecl-contact-frame").forEach((frame, i) => {
        gsap.from(frame, {
          rotateX: -60,
          y: 50,
          opacity: 0,
          duration: 1.2,
          delay: i * 0.1,
          ease: "expo.out",
          scrollTrigger: { trigger: ".ecl-contact-section", start: "top 75%", toggleActions: "play reverse play reverse" },
        });
      });
      gsap.from(".ecl-contact-title", { y: 30, opacity: 0, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: ".ecl-contact-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT V: EXPOSURE DATE — counter + lines draw ────────────────
      const dayEl = document.querySelector<HTMLElement>(".ecl-num-day");
      const monthEl = document.querySelector<HTMLElement>(".ecl-num-month");
      if (dayEl && monthEl && d) {
        const obj = { d: 0, m: 0 };
        gsap.to(obj, {
          d: d.getDate(), m: d.getMonth() + 1,
          duration: 1.5, ease: "power3.out",
          scrollTrigger: { trigger: ".ecl-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            dayEl.textContent = Math.round(obj.d).toString().padStart(2, "0");
            monthEl.textContent = Math.round(obj.m).toString().padStart(2, "0");
          },
        });
      }
      gsap.from(".ecl-exif-row", { x: -40, opacity: 0, stagger: 0.12, duration: 1, ease: "expo.out",
        scrollTrigger: { trigger: ".ecl-date-section", start: "top 65%", toggleActions: "play reverse play reverse" } });
      gsap.from(".ecl-date-bar", { scaleX: 0, transformOrigin: (i) => i === 0 ? "left" : "right", duration: 1.4, ease: "expo.inOut",
        scrollTrigger: { trigger: ".ecl-date-section", start: "top 65%", toggleActions: "play reverse play reverse" } });

      // ── ACT VI: Form ───────────────────────────────────────────────
      gsap.from(".ecl-form-card", { opacity: 0, y: 60, rotateX: -15, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".ecl-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Aperture component — 8 SVG blades that rotate from outside-in
  const Aperture = ({ blades = 8, size = 800, accent = "#d4a04e", bladeClass = "ecl-blade", className = "" }: { blades?: number; size?: number; accent?: string; bladeClass?: string; className?: string }) => {
    const r = size / 2;
    return (
      <svg className={className} viewBox={`0 0 ${size} ${size}`} style={{ width: "100%", height: "100%" }}>
        <defs>
          <clipPath id={`ecl-circle-${bladeClass}`}><circle cx={r} cy={r} r={r} /></clipPath>
        </defs>
        <g clipPath={`url(#ecl-circle-${bladeClass})`}>
          {Array.from({ length: blades }).map((_, i) => {
            const baseAngle = (i / blades) * 360;
            return (
              <g key={i} transform={`rotate(${baseAngle} ${r} ${r})`}>
                <g className={bladeClass}>
                  {/* Each blade is a triangle that pivots from outer corner */}
                  <polygon
                    points={`${r},${r} ${size * 1.5},${r * 0.2} ${size * 1.5},${r * 1.8}`}
                    fill="#0a0807"
                    stroke={accent}
                    strokeWidth="0.5"
                    opacity="0.95"
                  />
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    );
  };

  return (
    <main className="relative overflow-x-hidden text-white" style={{ background: "#0a0807", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes eclMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes eclMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* ═══ HEADER ═══════════════════════════════════════════════════ */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em] text-[#d4a04e]"
        style={{ background: "rgba(10, 8, 7, 0.6)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(212, 160, 78, 0.2)" }}>
        <span>● REC · Eclipse</span>
        <span className="text-white/40">{time24}</span>
        <span>{monthShort} {day}, {year}</span>
      </header>

      {/* ═══ ACT I: HERO with APERTURE ════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee — EXIF data */}
        <div className="ecl-marquee-t relative h-10 border-b border-[#d4a04e]/20 overflow-hidden flex items-center mt-12">
          <div className="whitespace-nowrap text-[10px] tracking-[0.4em] uppercase" style={{ color: "#d4a04e", animation: "eclMQ_L 35s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ISO 100 ◇ f/1.4 ◇ 1/250s ◇ 85mm ◇ {title.toUpperCase()} ◇ FRAME {i + 1} ◇
              </span>
            ))}
          </div>
        </div>

        {/* Main stage with aperture */}
        <div className="flex-1 flex items-center justify-center relative">
          {/* Background image */}
          <div className="absolute inset-0">
            <img src={galleryImages[0]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.3) brightness(0.5) contrast(1.1)" }} />
            <div className="absolute inset-0 bg-gradient-radial-vignette" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(10,8,7,0.95))" }} />
          </div>

          {/* Aperture overlay (8 blades) */}
          <div className="absolute inset-0 pointer-events-none">
            <Aperture blades={8} size={1600} bladeClass="ecl-blade" />
          </div>

          {/* Title content (visible through aperture) */}
          <div className="relative z-10 text-center px-6 max-w-4xl">
            <p className="ecl-hero-meta text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#d4a04e" }}>— Exposure 001 —</p>

            <h1 className="ecl-hero-title font-light leading-[0.85] tracking-[-0.04em]" style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(4rem, 13vw, 11rem)",
              textShadow: "0 0 60px rgba(212, 160, 78, 0.2)",
            }}>
              {title}
            </h1>

            <div className="ecl-hero-meta mt-12 flex items-center justify-center gap-6 text-xs uppercase tracking-[0.5em] text-white/70">
              <span>{monthShort}</span>
              <span style={{ color: "#d4a04e" }}>◆</span>
              <span className="tabular-nums">{day}</span>
              <span style={{ color: "#d4a04e" }}>◆</span>
              <span>{year}</span>
            </div>
            <p className="ecl-hero-meta mt-3 italic text-white/40" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
              {location?.split(",")[0] ?? "—"}
            </p>
          </div>

          {/* Camera UI — focus brackets at center */}
          <div className="ecl-focus-brackets absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="relative" style={{ width: 320, height: 320 }}>
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2" style={{ borderColor: "#d4a04e" }} />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2" style={{ borderColor: "#d4a04e" }} />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2" style={{ borderColor: "#d4a04e" }} />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2" style={{ borderColor: "#d4a04e" }} />
            </div>
          </div>

          {/* Camera UI corner labels */}
          <div className="ecl-ui-tl absolute top-20 left-8 text-[10px] uppercase tracking-[0.4em] flex flex-col gap-1" style={{ color: "#d4a04e" }}>
            <div className="flex items-center gap-2"><span className="text-white/40">F</span> 1.4</div>
            <div className="flex items-center gap-2"><span className="text-white/40">ISO</span> 100</div>
          </div>
          <div className="ecl-ui-tr absolute top-20 right-8 text-right text-[10px] uppercase tracking-[0.4em]" style={{ color: "#d4a04e" }}>
            <div>1/250s</div>
            <div>85mm</div>
          </div>
          <div className="ecl-ui-bl absolute bottom-16 left-8 text-[10px] uppercase tracking-[0.4em] flex items-center gap-2" style={{ color: "#d4a04e" }}>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span>REC</span>
          </div>
          <div className="ecl-ui-br absolute bottom-16 right-8 text-right text-[10px] uppercase tracking-[0.4em]" style={{ color: "#d4a04e" }}>
            <div>+0.3 EV</div>
            <div>AWB</div>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="ecl-marquee-b relative h-10 border-t border-[#d4a04e]/20 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.4em] uppercase text-white/60" style={{ animation: "eclMQ_R 40s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ◇ RAW ◇ 4096×2160 ◇ ARRI ALEXA ◇ COOKE ANAMORPHIC ◇ {title.toUpperCase()} ◇
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED APERTURE TRANSITION ═══════════════════════ */}
      <section className="ecl-ap-section relative h-screen overflow-hidden bg-[#0a0807]">
        {/* 4 scene backgrounds layered */}
        {[
          { src: galleryImages[0], label: "Frame 001 · 2024" },
          { src: galleryImages[1], label: "Frame 045 · Summer" },
          { src: galleryImages[2], label: "Frame 132 · Voyage" },
          { src: galleryImages[3], label: "Frame 200 · Today" },
        ].map((scene, i) => (
          <div key={i} className={`ecl-scene-${i} absolute inset-0`} style={{ opacity: i === 0 ? 1 : 0 }}>
            <img src={scene.src} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.4) contrast(1.1) brightness(0.7)" }} />
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(10,8,7,0.7))" }} />
          </div>
        ))}

        {/* Aperture overlay for this section */}
        <div className="absolute inset-0 pointer-events-none">
          <Aperture blades={8} size={1800} bladeClass="ecl-ap-blade" />
        </div>

        {/* Stage indicator (center) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
          <p className="ecl-stage-time text-[10px] uppercase tracking-[0.5em] tabular-nums" style={{ color: "#d4a04e" }}>1/4000</p>
          <p className="ecl-stage-label font-light italic mt-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>Sözleştik</p>
        </div>

        {/* Corner labels */}
        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#d4a04e" }}>II · SHUTTER</div>
        <div className="absolute top-12 right-12 text-[10px] uppercase tracking-[0.5em] text-white/40">SCROLL TO ADVANCE</div>
      </section>

      {/* ═══ ACT III: FILM DEVELOPING ═════════════════════════════════ */}
      <section className="ecl-develop-section relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="ecl-develop-label text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#d4a04e" }}>III · DEVELOPING</p>
          <h2 className="ecl-develop-label text-center font-light italic leading-tight mb-20" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            From silver to color.
          </h2>

          <div className="grid sm:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <figure key={i} className="relative">
                <div className="aspect-[4/5] overflow-hidden border border-[#d4a04e]/20">
                  <img src={galleryImages[i]} alt="" className="ecl-develop w-full h-full object-cover" />
                </div>
                <figcaption className="ecl-develop-label mt-4 flex justify-between items-baseline text-[10px] uppercase tracking-[0.4em]">
                  <span style={{ color: "#d4a04e" }}>Roll 01 · #{String(i + 1).padStart(3, "0")}</span>
                  <span className="text-white/40">{["Spring", "Summer", "Today"][i]}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: CONTACT SHEET ════════════════════════════════════ */}
      <section className="ecl-contact-section relative py-32 px-6" style={{ background: "#080605" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline justify-between mb-12">
            <div>
              <p className="ecl-contact-title text-[10px] uppercase tracking-[0.5em] mb-2" style={{ color: "#d4a04e" }}>IV · CONTACT SHEET</p>
              <h2 className="ecl-contact-title font-light italic leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                Six selected frames.
              </h2>
            </div>
            <p className="ecl-contact-title text-[10px] uppercase tracking-[0.4em] text-white/40 hidden sm:block">Tri-X 400 · Push +1</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" style={{ perspective: 1600 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="ecl-contact-frame relative aspect-[4/5] overflow-hidden hover:scale-[1.02] transition-transform duration-500" style={{
                background: "#0a0807",
                border: "1px solid rgba(212, 160, 78, 0.2)",
                boxShadow: "0 16px 32px rgba(0,0,0,0.6)",
              }}>
                <img src={galleryImages[i % galleryImages.length]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.4) contrast(1.05)" }} />
                {/* Frame number */}
                <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] tabular-nums" style={{ color: "#d4a04e" }}>
                  {String(i + 1).padStart(2, "0")}A
                </div>
                {/* Sprocket holes effect on edges */}
                <div className="absolute top-0 left-0 right-0 h-3 flex justify-around items-center bg-[#0a0807]">
                  {Array(8).fill(null).map((_, k) => <div key={k} className="w-1.5 h-1.5 rounded-sm" style={{ background: "#d4a04e", opacity: 0.4 }} />)}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-3 flex justify-around items-center bg-[#0a0807]">
                  {Array(8).fill(null).map((_, k) => <div key={k} className="w-1.5 h-1.5 rounded-sm" style={{ background: "#d4a04e", opacity: 0.4 }} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT V: EXPOSURE DATE ═════════════════════════════════════ */}
      <section className="ecl-date-section relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-12" style={{ color: "#d4a04e" }}>V · EXPOSURE DETAILS</p>

          <div className="flex items-center justify-center gap-4 sm:gap-12 mb-16">
            <div className="ecl-date-bar h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, #d4a04e)" }} />
            <p className="ecl-num-day font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="font-light text-white/30 leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 9vw, 8rem)" }}>·</div>
            <p className="ecl-num-month font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="ecl-date-bar h-px flex-1" style={{ background: "linear-gradient(-90deg, transparent, #d4a04e)" }} />
          </div>

          {/* EXIF-style rows */}
          <div className="max-w-md mx-auto space-y-3 font-mono text-sm">
            {[
              { k: "Year", v: year.toString() },
              { k: "Time", v: time24 },
              { k: "Day", v: d?.toLocaleString("en-US", { weekday: "long" }) ?? "—" },
              { k: "Location", v: location?.split(",")[0] ?? "—" },
              { k: "Camera", v: title.replace("&", "×") },
              { k: "Mode", v: "Eternity" },
            ].map((row, i) => (
              <div key={i} className="ecl-exif-row flex justify-between items-baseline border-b border-white/10 pb-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">{row.k}</span>
                <span className="text-base" style={{ color: "#d4a04e" }}>{row.v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: RSVP ════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="ecl-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out"
          style={{
            background: "linear-gradient(135deg, rgba(212, 160, 78, 0.08) 0%, rgba(212, 160, 78, 0.02) 100%)",
            border: "1px solid rgba(212, 160, 78, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(212, 160, 78, 0.2), 0 40px 100px rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(20px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Shutter-button center decoration */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full flex items-center justify-center" style={{
            background: "linear-gradient(180deg, #1a1310, #0a0807)",
            border: "2px solid #d4a04e",
            boxShadow: "0 8px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(212, 160, 78, 0.3)",
          }}>
            <div className="w-6 h-6 rounded-full bg-red-600 animate-pulse" />
          </div>

          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#d4a04e" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#d4a04e" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#d4a04e" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#d4a04e" }} />

          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3 mt-4" style={{ color: "#d4a04e" }}>VI · CAPTURE</p>
          <h3 className="text-center font-light italic mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Press the shutter
          </h3>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#d4a04e" }} />
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
